import React, { memo, useMemo } from "react";
import { HStack, Text, Highlight, Stack } from "@chakra-ui/react";
import colors from "app/colors";
import UserAvatar from "../_shared/user-avatar";
import { UserFragment } from "gql/graphql";
import {
  isUserSmsOnlyAndNotLoggedIn,
  isUserInactive,
  isUserOutOfOffice,
  getUserOutOfOfficeNote,
  getUserMention,
  getUserFullName,
} from "modules/utils/Functions";

interface IProps {
  user?: UserFragment;
  search: string;
}

const UserOptionLabel = ({ user, search }: IProps) => {
  const userNote = useMemo(() => {
    if (!user) return null;
    if (isUserSmsOnlyAndNotLoggedIn(user)) {
      return "(via SMS)";
    }
    if (isUserInactive(user)) {
      return "(Inactive)";
    }
    if (isUserOutOfOffice(user)) {
      return `${getUserOutOfOfficeNote(user.outOfOffice)}`;
    }
    return null;
  }, [user]);

  if (!user) return null;

  if (user.active) {
    return (
      <HStack color={colors.blue} spacing={2} h="32px">
        <UserAvatar userId={user.id} size="sm" displayOnlineIndicator />
        <Stack spacing={0} alignItems="flex-start">
          <HStack spacing={1}>
            <Text
              noOfLines={1}
              fontSize={14}
              color={colors.blue}
              fontWeight={700}
            >
              <Highlight
                query={search}
                styles={{
                  fontWeight: 700,
                  color: colors.blue,
                  margin: "0 !important",
                }}
              >
                {getUserMention(user)}
              </Highlight>
            </Text>
            <Text fontSize={14} color={colors.grayMain} fontWeight={500}>
              {userNote}
            </Text>
          </HStack>
          <Text
            noOfLines={1}
            fontSize={12}
            fontWeight={400}
            color={colors.gray50}
          >
            <Highlight
              query={search}
              styles={{
                fontWeight: 700,
                color: colors.gray50,
                margin: "0 !important",
              }}
            >
              {getUserFullName(user)}
            </Highlight>
          </Text>
        </Stack>
      </HStack>
    );
  }

  return (
    <HStack color={colors.grayMain} spacing={2} h="32px">
      <UserAvatar userId={user.id} size="sm" displayOnlineIndicator={false} />
      <Stack spacing={0} alignItems="flex-start">
        <HStack spacing={1}>
          <Text
            noOfLines={1}
            fontSize={14}
            color={colors.grayMain}
            fontWeight={700}
            fontStyle="italic"
          >
            <Highlight
              query={search}
              styles={{
                fontWeight: 700,
                color: colors.blue,
                margin: "0 !important",
              }}
            >
              {getUserMention(user)}
            </Highlight>
          </Text>
          <Text fontSize={14} color={colors.grayMain} fontWeight={500}>
            {userNote}
          </Text>
        </HStack>
        <Text
          noOfLines={1}
          fontSize={12}
          fontWeight={400}
          color={colors.gray50}
        >
          <Highlight
            query={search}
            styles={{
              fontWeight: 700,
              color: colors.gray50,
              margin: "0 !important",
            }}
          >
            {getUserFullName(user)}
          </Highlight>
        </Text>
      </Stack>
    </HStack>
  );
};

export default memo(UserOptionLabel);
